import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../utils/apiUtils";
import { 
  FaWallet, 
  FaCreditCard, 
  FaUsers, 
  FaCar, 
  FaCalendarAlt, 
  FaUserCog, 
  FaCheckCircle, 
  FaClock, 
  FaArrowUp, 
  FaArrowDown, 
  FaBuilding, 
  FaReceipt,
  FaChevronRight,
  FaUserShield
} from "react-icons/fa";

const CorporateDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const [wallet, setWallet] = useState(null);
  const [walletTx, setWalletTx] = useState([]);

  const [staffCount, setStaffCount] = useState(0);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [bookingsCount, setBookingsCount] = useState(0);

  const fetchMe = async () => {
    setLoading(true);
    try {
      const res = await api.get("/corporate/me");
      if (res?.data?.success) {
        const memberData = res.data.data;
        setMe(memberData);
        // Update localStorage with fresh member data to prevent stale data display
        localStorage.setItem("corporateMember", JSON.stringify(memberData));
      } else {
        toast.error(res?.data?.message || "Failed to load profile");
        localStorage.removeItem("corporateToken");
        localStorage.removeItem("corporateMember");
        navigate("/corporate/login", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load profile");
      localStorage.removeItem("corporateToken");
      localStorage.removeItem("corporateMember");
      navigate("/corporate/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  useEffect(() => {
    document.title = "Cabzigo-home | Cabzigo Admin";
  }, []);

  useEffect(() => {
    if (!me) return;

    if (me.role !== "admin") {
      navigate("/corporate/bookings", { replace: true });
      return;
    }

    (async () => {
      try {
        const bookingsRes = await api.get("/corporate/bookings");
        if (bookingsRes?.data?.success) {
          setBookingsCount((bookingsRes.data.data || []).length);
        }
      } catch (e) {
      }

      try {
        const staffRes = await api.get("/corporate/staff");
        if (staffRes?.data?.success) {
          setStaffCount((staffRes.data.data || []).length);
        }
      } catch (e) {
      }

      try {
        const approvalsRes = await api.get("/corporate/bookings?status=pending_corporate_approval");
        if (approvalsRes?.data?.success) {
          setPendingApprovalsCount((approvalsRes.data.data || []).length);
        }
      } catch (e) {
      }

      try {
        const walletRes = await api.get("/corporate/wallet");
        if (walletRes?.data?.success) {
          setWallet(walletRes.data.data);
        }
      } catch (e) {
      }

      try {
        const txRes = await api.get("/corporate/wallet/transactions");
        if (txRes?.data?.success) {
          const list = Array.isArray(txRes.data.data) ? txRes.data.data : [];
          setWalletTx(list.slice(0, 10));
        }
      } catch (e) {
      }
    })();
  }, [me, navigate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isCredit = (type) => {
    const t = String(type || "").toLowerCase();
    return t === "credit" || t === "refund" || t === "add_funds" || t === "deposit";
  };

  const renderStatusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "success" || s === "completed") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <FaCheckCircle className="text-[10px]" /> Success
        </span>
      );
    }
    if (s === "pending") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
          <FaClock className="text-[10px]" /> Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
        {status || "Failed"}
      </span>
    );
  };

  const renderTypeCell = (type) => {
    const credit = isCredit(type);
    return (
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-lg ${credit ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
          {credit ? <FaArrowUp className="text-xs" /> : <FaArrowDown className="text-xs" />}
        </div>
        <span className="font-semibold text-slate-700 capitalize">{type?.replace('_', ' ')}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F56753]"></div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600">Please login again.</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1200px] mx-auto pb-12">
      {/* Welcome Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-red-950 text-white rounded-2xl shadow-xl p-6 md:p-8 mb-8 border border-slate-800 hover-float">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-56 h-56 bg-[#F56753]/10 rounded-full blur-3xl pointer-events-none animate-pulse"></div>
        <div className="absolute bottom-0 left-0 -mb-6 -ml-6 w-72 h-72 bg-[#F56753]/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <span className="px-3 py-1 bg-[#F56753]/20 text-red-300 rounded-full text-xs font-semibold tracking-wide uppercase border border-[#F56753]/30">
              Corporate Account
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-2 bg-gradient-to-r from-white via-slate-100 to-slate-300 bg-clip-text text-transparent">
              Cabzigo-home
            </h1>
            <p className="text-slate-300 mt-1 font-medium flex items-center gap-2 text-sm md:text-base">
              <FaBuilding className="text-red-400" /> {me?.companyName || "Your Company"}
            </p>
          </div>
          <div className="flex flex-col md:items-end justify-center text-slate-300 bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10">
            <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Signed in as</span>
            <span className="text-sm font-bold text-white mt-0.5">{me?.email}</span>
            <span className="text-xs px-2 py-0.5 mt-1 bg-[#F56753]/20 text-red-300 border border-[#F56753]/30 rounded-full font-medium self-start md:self-end capitalize">
              Role: {me?.role}
            </span>
          </div>
        </div>
      </div>

      {/* Corporate Overview Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Column 1: Virtual Card */}
        {me?.billingModel === 'wallet' ? (
          <div className="relative overflow-hidden bg-gradient-to-br from-[#F56753] via-red-800 to-red-950 text-white rounded-2xl shadow-xl p-6 border border-[#F56753]/30 transition-all duration-300 hover:shadow-2xl hover:scale-[1.01] flex flex-col justify-between h-[210px] w-full">
            {/* Card Holographic/Glass background glow */}
            <div className="absolute top-0 right-0 w-36 h-36 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
            
            {/* Card Header */}
            <div className="flex justify-between items-start relative z-10">
              <div>
                <p className="text-xs text-red-200 uppercase tracking-widest font-semibold">Cabzigo Wallet</p>
                <p className="text-xs text-red-200 mt-0.5">Corporate Member</p>
              </div>
              {/* Chip Icon */}
              <div className="w-10 h-8 bg-gradient-to-br from-yellow-300 to-amber-500 rounded-md relative shadow-inner overflow-hidden border border-yellow-200/40">
                <div className="absolute inset-x-2 top-2 bottom-2 border border-amber-950/20 rounded"></div>
                <div className="absolute inset-y-2 left-2 right-2 border border-amber-950/20 rounded"></div>
                <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-amber-950/20"></div>
                <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-amber-950/20"></div>
              </div>
            </div>

            {/* Card Body */}
            <div className="my-auto relative z-10">
              <p className="text-xs text-red-200/80 uppercase tracking-wider">Available Balance</p>
              <h2 className="text-3xl font-extrabold tracking-tight mt-1">
                {formatCurrency(wallet?.walletBalance || 0)}
              </h2>
            </div>

            {/* Card Footer */}
            <div className="flex justify-between items-end relative z-10">
              <div>
                <p className="text-[10px] text-red-200/70 uppercase tracking-wider">Card Holder</p>
                <p className="text-xs font-semibold tracking-wide truncate max-w-[180px]">{me?.companyName || "Cabzigo Corp"}</p>
              </div>
              <div className="text-right">
                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded text-[10px] font-semibold uppercase tracking-wider">
                  {wallet?.walletStatus || "Active"}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-red-950 text-white rounded-2xl shadow-xl p-6 border border-slate-700 transition-all duration-300 hover:shadow-2xl hover:scale-[1.01] flex flex-col justify-between h-[210px] w-full">
            {/* Card Holographic/Glass background glow */}
            <div className="absolute top-0 right-0 w-36 h-36 bg-[#F56753]/10 rounded-full blur-2xl pointer-events-none"></div>
            
            {/* Card Header */}
            <div className="flex justify-between items-start relative z-10">
              <div>
                <p className="text-xs text-red-300 uppercase tracking-widest font-semibold">Cabzigo Credit</p>
                <p className="text-[10px] text-slate-400 mt-1 font-semibold">
                  Limit: {formatCurrency(wallet?.creditLimit || me?.creditLimit || 0)} + Funds: {formatCurrency(wallet ? wallet.addedFunds : (me?.walletBalance || 0))}
                </p>
              </div>
              {/* Chip Icon */}
              <div className="w-10 h-8 bg-gradient-to-br from-yellow-300 to-amber-500 rounded-md relative shadow-inner overflow-hidden border border-yellow-200/40">
                <div className="absolute inset-x-2 top-2 bottom-2 border border-amber-950/20 rounded"></div>
                <div className="absolute inset-y-2 left-2 right-2 border border-amber-950/20 rounded"></div>
                <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-amber-950/20"></div>
                <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-amber-950/20"></div>
              </div>
            </div>

            {/* Card Body */}
            <div className="my-auto relative z-10">
              <p className="text-xs text-red-300/85 uppercase tracking-wider">Available Balance</p>
              <h2 className="text-3xl font-extrabold tracking-tight mt-1 text-emerald-400">
                {formatCurrency(wallet ? (wallet.totalAvailable ?? wallet.availableCredit) : Math.max(0, (me?.creditLimit || 0) + (me?.walletBalance || 0) - (me?.creditUsed || 0)))}
              </h2>
            </div>

            {/* Card Footer */}
            <div className="flex justify-between items-end relative z-10">
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Card Holder</p>
                <p className="text-xs font-semibold tracking-wide truncate max-w-[180px]">{me?.companyName || "Cabzigo Corp"}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-red-300 uppercase tracking-wider">Credit Used</p>
                <p className="text-xs font-bold text-red-300">
                  {formatCurrency((wallet?.creditUsed || me?.creditUsed || 0) + (wallet?.reservedFunds || 0))}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Column 2: Program & Account Status */}
        <div className="bg-white rounded-2xl shadow-md p-6 border border-slate-100 flex flex-col justify-between h-[210px] hover:shadow-lg transition-shadow">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Billing Program</span>
              <span className="px-2.5 py-1 bg-red-50 text-[#F56753] rounded-full text-xs font-bold capitalize flex items-center gap-1.5 border border-red-100">
                <FaReceipt className="text-[10px]" /> {me?.billingModel} Account
              </span>
            </div>
            
            <div className="space-y-2 mt-3">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500 font-medium">Program Status</span>
                <span className="text-emerald-600 font-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                  Active & Verified
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500 font-medium">Corporate ID</span>
                <span className="font-mono text-slate-650 font-bold bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                  {me?._id?.substring(me?._id.length - 8).toUpperCase() || "CABZ-CORP"}
                </span>
              </div>
              {me?.billingModel === 'credit' && (
                <>
                  <div className="flex justify-between items-center text-xs border-t border-slate-100/70 pt-1.5">
                    <span className="text-slate-500 font-medium">Credit Limit</span>
                    <span className="font-semibold text-slate-700">{formatCurrency(wallet?.creditLimit || me?.creditLimit || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-500 font-medium">Added Funds</span>
                    <span className="font-semibold text-emerald-600">+{formatCurrency(wallet?.addedFunds || 0)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between items-center text-xs border-t border-slate-100/70 pt-1.5">
                <span className="text-slate-500 font-medium">Billing Currency</span>
                <span className="font-semibold text-slate-705">INR (₹)</span>
              </div>
            </div>
          </div>

          <div className="text-xs text-slate-400 flex items-center gap-1.5 border-t border-slate-50 pt-3">
            <FaUserShield className="text-slate-400" /> Account Managed by Admin Portal
          </div>
        </div>

        {/* Column 3: Corporate Usage Stats */}
        <div className="bg-white rounded-2xl shadow-md p-6 border border-slate-100 flex flex-col justify-between h-[210px] hover:shadow-lg transition-shadow">
          <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Usage & Statistics</span>
          
          <div className="grid grid-cols-3 gap-2 text-center my-auto">
            <div className="p-2 bg-slate-50 rounded-xl border border-slate-100 hover:bg-red-50/50 hover:border-red-100 transition-colors">
              <span className="block text-xl font-extrabold text-slate-800">{bookingsCount}</span>
              <span className="text-[10px] text-slate-500 font-medium mt-1 block">Rides</span>
            </div>
            
            <div className="p-2 bg-slate-50 rounded-xl border border-slate-100 hover:bg-red-50/50 hover:border-red-100 transition-colors">
              <span className="block text-xl font-extrabold text-slate-800">{staffCount}</span>
              <span className="text-[10px] text-slate-500 font-medium mt-1 block">Staff</span>
            </div>

            <div className="p-2 bg-slate-50 rounded-xl border border-slate-100 hover:bg-amber-50/50 hover:border-amber-100 transition-colors">
              <span className={`block text-xl font-extrabold ${pendingApprovalsCount > 0 ? "text-amber-600 animate-bounce" : "text-slate-800"}`}>
                {pendingApprovalsCount}
              </span>
              <span className="text-[10px] text-slate-500 font-medium mt-1 block">Pending</span>
            </div>
          </div>

          <div className="text-xs text-slate-400 flex items-center gap-1.5 border-t border-slate-50 pt-3">
            <FaCalendarAlt className="text-slate-400" /> Stats updated dynamically
          </div>
        </div>
      </div>

      {/* Quick Actions Panel */}
      <div className="mb-8">
        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <span className="w-1.5 h-5 bg-[#F56753] rounded-full"></span>
          Quick Actions & Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          
          {/* Action 1: Book Ride */}
          {me?.role === "admin" && (
            <Link 
              to="/corporate/book-ride" 
              className="group relative overflow-hidden bg-gradient-to-br from-red-50 to-red-100/50 rounded-2xl p-5 border border-red-200/60 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1"
            >
              <div className="absolute -right-6 -bottom-6 text-[#F56753]/5 text-8xl font-bold group-hover:scale-110 transition-transform">
                <FaCar />
              </div>
              <div className="flex justify-between items-start">
                <div className="p-3 bg-[#F56753] text-white rounded-xl shadow-md shadow-red-100">
                  <FaCar className="text-lg" />
                </div>
                <span className="text-xs font-bold text-[#F56753] bg-red-100 px-2 py-0.5 rounded-full uppercase tracking-wider">Book Now</span>
              </div>
              <h3 className="text-base font-bold text-red-950 mt-4 group-hover:text-[#F56753] transition-colors">Book Corporate Ride</h3>
              <p className="text-xs text-[#F56753]/70 mt-1">Schedule instant or planned travels for staff members.</p>
              <div className="mt-4 flex items-center text-xs font-bold text-[#F56753] gap-1 group-hover:gap-2 transition-all">
                Launch Booking <FaChevronRight className="text-[10px]" />
              </div>
            </Link>
          )}

          {/* Action 2: Staff Management */}
          {me?.role === "admin" && (
            <Link 
              to="/corporate/staff" 
              className="group relative overflow-hidden bg-gradient-to-br from-red-50/70 to-red-100/30 rounded-2xl p-5 border border-red-100 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1"
            >
              <div className="absolute -right-6 -bottom-6 text-[#F56753]/5 text-8xl font-bold group-hover:scale-110 transition-transform">
                <FaUsers />
              </div>
              <div className="flex justify-between items-start">
                <div className="p-3 bg-[#F56753] text-white rounded-xl shadow-md shadow-red-100">
                  <FaUsers className="text-lg" />
                </div>
                <span className="text-xs font-bold text-[#F56753] bg-red-100 px-2 py-0.5 rounded-full uppercase tracking-wider">Manage</span>
              </div>
              <h3 className="text-base font-bold text-red-950 mt-4 group-hover:text-[#F56753] transition-colors">Manage Employees</h3>
              <p className="text-xs text-[#F56753]/70 mt-1">Add, review, and authorize permissions for your staff.</p>
              <div className="mt-4 flex items-center text-xs font-bold text-[#F56753] gap-1 group-hover:gap-2 transition-all">
                View Staff Members <FaChevronRight className="text-[10px]" />
              </div>
            </Link>
          )}

          {/* Action 3: Wallet Details */}
          {me?.role === "admin" && (
            <Link 
              to="/corporate/wallet" 
              className="group relative overflow-hidden bg-gradient-to-br from-emerald-50/70 to-emerald-100/30 rounded-2xl p-5 border border-emerald-100 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1"
            >
              <div className="absolute -right-6 -bottom-6 text-emerald-500/5 text-8xl font-bold group-hover:scale-110 transition-transform">
                <FaWallet />
              </div>
              <div className="flex justify-between items-start">
                <div className="p-3 bg-emerald-600 text-white rounded-xl shadow-md shadow-emerald-150">
                  <FaWallet className="text-lg" />
                </div>
                <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full uppercase tracking-wider">Finance</span>
              </div>
              <h3 className="text-base font-bold text-emerald-950 mt-4 group-hover:text-emerald-600 transition-colors">Wallet & Payments</h3>
              <p className="text-xs text-emerald-800/70 mt-1">View transaction logs, add funds, or review billing limits.</p>
              <div className="mt-4 flex items-center text-xs font-bold text-emerald-600 gap-1 group-hover:gap-2 transition-all">
                Wallet Dashboard <FaChevronRight className="text-[10px]" />
              </div>
            </Link>
          )}

          {/* Action 4: Profile & Settings */}
          <Link 
            to="/corporate/profile" 
            className="group relative overflow-hidden bg-gradient-to-br from-slate-50/70 to-slate-100/30 rounded-2xl p-5 border border-slate-100 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1"
          >
            <div className="absolute -right-6 -bottom-6 text-slate-500/5 text-8xl font-bold group-hover:scale-110 transition-transform">
              <FaUserCog />
            </div>
            <div className="flex justify-between items-start">
              <div className="p-3 bg-slate-700 text-white rounded-xl shadow-md shadow-slate-200">
                <FaUserCog className="text-lg" />
              </div>
              <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-wider">Settings</span>
            </div>
            <h3 className="text-base font-bold text-slate-950 mt-4 group-hover:text-slate-600 transition-colors">Corporate Profile</h3>
            <p className="text-xs text-slate-800/70 mt-1">Update company info, billing address, and admin contacts.</p>
            <div className="mt-4 flex items-center text-xs font-bold text-slate-600 gap-1 group-hover:gap-2 transition-all">
              Go to Profile <FaChevronRight className="text-[10px]" />
            </div>
          </Link>

        </div>
      </div>

      {/* Transactions Section */}
      <div className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Recent Wallet Transactions</h2>
            <p className="text-xs text-slate-500 mt-0.5">Showing last 10 activities on this account</p>
          </div>
          <Link 
            to="/corporate/bookings" 
            className="text-xs font-bold text-[#F56753] bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors"
          >
            View Bookings
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50/70">
              <tr>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Date & Time</th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Booking ID</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {walletTx.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-slate-400 text-sm font-medium">
                    No transactions yet on this account.
                  </td>
                </tr>
              ) : (
                walletTx.map((t) => (
                  <tr key={t._id} className="hover:bg-slate-50/40 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {formatDate(t.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {renderTypeCell(t.type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-900">
                      <span className={`text-sm font-extrabold ${isCredit(t.type) ? "text-emerald-600" : "text-rose-600"}`}>
                        {isCredit(t.type) ? "+" : "-"} {formatCurrency(t.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {renderStatusBadge(t.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {t.bookingId ? (
                        <span className="font-mono text-xs font-bold text-slate-700 bg-slate-50 px-2 py-1 rounded border border-slate-200/60 shadow-sm">
                          {typeof t.bookingId === 'object' && t.bookingId !== null
                            ? (t.bookingId.bookingId || t.bookingId.customBookingId || t.bookingId.bookingNumber || t.bookingId._id || "-")
                            : (t.bookingId || "-")}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CorporateDashboard;
